import { useContext, useState, useReducer } from "react";
import { DateTime } from "luxon";
import Layout from "../layouts/Default";
import Input from "../components/inputs/Input";
import Textarea from "../components/inputs/Textarea";
import TypesContext from "../contexts/types";
import AuthContext from "../contexts/auth";
import Button from "../components/buttons/Button";
import RadioGroup from "../components/radioButtons/RadioGroup";
import RadioButton from "../components/radioButtons/RadioButton";
import fetchApi from "../fetchApi";
import Text from "../components/typography/Text";
import Link from "../components/links/Link";

const TOOL_URL =
  "https://enabledbydesign.ch/wp-content/uploads/2019/10/EBD_Design-brief-3.pdf";

export default ({ history }) => {
  const { mission = null } = history.location.state
    ? history.location.state
    : {};
  const { skills } = useContext(TypesContext);
  const [name, setName] = useState(mission?.title?.rendered ?? "");
  const [description, setDescription] = useState(mission?.description ?? "");
  const missionSkills = mission?.skill || [];
  const [selectedSkill, selectSkill] = useState(missionSkills?.[0]);
  const [postingDate, setDeadline] = useState(
    mission?.postingDate
      ? DateTime.fromFormat(mission.postingDate, "y/M/d").toFormat("d/M/y")
      : DateTime.local().toFormat("d/M/y")
  );
  const { user } = useContext(AuthContext);
  const isUniversity = !!user?.ecosystem;
  const [errors, setError] = useReducer(reduceError, []);
  const [loading, setLoading] = useState(false);

  const onPublish = async () => {
    setError();
    const postingDateMoment = DateTime.fromFormat(postingDate, "d/M/y");
    if (!name) {
      setError({ field: "name", message: "Missing name field" });
    } else if (!description) {
      setError({ field: "description", message: "Missing design scope" });
    } else if (
      postingDate &&
      (!dateRegex.test(postingDate) || !postingDateMoment.isValid)
    ) {
      setError({
        field: "postingDate",
        message: "Please check the postingDate date format : DD/MM/YYYY",
      });
    } else {
      setLoading(true);
      try {
        const formData = new FormData();
        formData.append("title", name);
        formData.append("description", description);
        formData.append("skill", selectedSkill);
        formData.append("postingDate", postingDateMoment.toFormat("y/M/d"));
        const editID = mission ? `/${mission.id}` : "";
        const result = await fetchApi(
          `/mission${editID}`,
          {
            method: "post",
            body: formData,
          },
          false
        );

        if (result.hasOwnProperty("data") && result.data.status !== 200) {
          setError({ message: result.message });
        } else {
          const notice = mission ? "update" : result.status;
          history.push({
            pathname: `/project/${user.slug}`,
            state: {
              notice: notice,
            },
          });
        }
      } catch (error) {
        setLoading(false);
        console.error(error);
        setError({ message: "Can't create new mission" });
      }
    }
  };

  return (
    <Layout title="Edit design project">
      <div className="pEditMission">
        <div className="pEditMission_content">
          <h3>{mission ? "Edit design job" : "Post design job"}</h3>
          <Input
            label="Objective of the design project"
            required
            max={80}
            value={name}
            setValue={setName}
            error={errors.some(({ field }) => field === "name")}
          />
          <div className="pEditMission_large">
            <Textarea
              label="Design scope"
              required
              value={description}
              setValue={setDescription}
              placeholder="Describe your need and why"
              notice={noticeDescription}
            />
          </div>
          <h4>Skill(s)</h4>
          <RadioGroup>
            <div className="pEditMission_list">
              {skills &&
                skills.map(skill => (
                  <RadioButton
                    name="skill"
                    key={skill.id}
                    label={skill.name}
                    notice={skill.description}
                    checked={selectedSkill === skill.id}
                    setValue={() => selectSkill(skill.id)}
                  />
                ))}
            </div>
          </RadioGroup>
          <div className="pEditMission_inputs">
            <Input
              label="Posting date"
              value={postingDate}
              setValue={setDeadline}
              error={errors.some(({ field }) => field === "postingDate")}
              icon="calendar"
              placeholder="DD/MM/YYYY"
            />
          </div>
          {isUniversity && (
            <div className="pEditMission_inputs">
              <Text>
                Don’t forget to fill the design brief tool before your first
                meeting.
              </Text>
              <Link
                href={TOOL_URL}
                style={{ fontSize: "18px", marginTop: "0.5rem" }}
              >
                Download design brief tool
              </Link>
            </div>
          )}
          {errors && errors.length > 0 && (
            <ul className="pEditMission_error">
              {errors.map((err, index) => (
                <li key={index}>{err.message}</li>
              ))}
            </ul>
          )}
          <div className="pEditMission_buttons">
            <Button big outlined onClick={() => history.goBack()}>
              Cancel
            </Button>
            {!loading && (
              <Button big onClick={onPublish}>
                Publish
              </Button>
            )}
            {loading && (
              <Button big disabled loading={loading}>
                Publish
              </Button>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

const reduceError = (state, action) =>
  action ? [...state, { ...action }] : [];

const dateRegex = /^[0-9]{1,4}\/[0-9]{1,2}\/[0-9]{1,4}$/;

const noticeDescription = (
  <>
    <div>Where would you like support from a designer?</div>
    <div>What can design bring to your project? </div>
    <div>What is the expected outcome? For which purpose?</div>
  </>
);
